import { Component, OnInit, AfterViewInit, Inject, Injector } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { TransitionAction, TransitionActionData } from '../../lib/transition-action.model';
import { WorkflowEditorStore } from '../../lib/workflow-editor.store';
import { FormField } from '../../lib/form-field.model';
import { QuillModules } from 'ngx-quill';

export interface WorkflowTransitionActionFormOptions {
  item: TransitionAction|undefined;
  templates: TransitionAction[];
  callerInjector?: Injector;
}

export type WorkflowTransitionActionFormResult = TransitionAction|string|undefined;

export function invalidPlaceholders(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (value) {
      const placeholders = value.match(/%(.*?)%|{{#(.*?)}}|{{\/(.*?)}}/g)
      // Does any placeholder contain a html tag?
      if (placeholders) {
        const invalidPlaceholders = placeholders.filter(placeholder => placeholder.match(/<.*?>/g));
        if (invalidPlaceholders.length > 0) {
          console.log(invalidPlaceholders);
          return { invalidPlaceholders: { value: invalidPlaceholders } };
        }
      }
    }
    return null;
  };
}

@Component({
    selector: 'app-workflow-transition-action-form',
    templateUrl: './action-form.component.html',
    styleUrls: ['./action-form.component.css'],
    standalone: false
})
export class WorkflowTransitionActionFormComponent implements OnInit, AfterViewInit {

  public quillModules: QuillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'align': [] }],
      ['link'],
      ['clean'],
    ]
  };

  public readonly form: FormGroup;

  public get formOptionsGroup(): FormGroup|undefined {
    return this.form.get('options') as FormGroup ?? undefined;
  }

  public set formOptionsGroup(group: FormGroup|undefined) {
    if (!group) {
      this.form.removeControl('options');
      return;
    }
    this.form.setControl('options', group);
  }

  public get formOptionsRefundOptionsArray(): FormArray|undefined {
    return this.formOptionsGroup?.get('refund_options') as FormArray|undefined;
  }

  public get formOptionsFillInFieldsArray(): FormArray|undefined {
    return this.formOptionsGroup?.get('fields') as FormArray|undefined;
  }

  public  readonly original?: TransitionAction;
  public           result?: TransitionAction;
  public  readonly templates: TransitionAction[];
  private readonly dialogRef: MatDialogRef<WorkflowTransitionActionFormOptions, WorkflowTransitionActionFormResult>;

  public availableFormFields: FormField[] = [];

  constructor(fb: FormBuilder,
              @Inject(MAT_DIALOG_DATA) data: WorkflowTransitionActionFormOptions,
              dialogRef: MatDialogRef<WorkflowTransitionActionFormOptions, WorkflowTransitionActionFormResult>) {
    this.dialogRef = dialogRef;

    const workflowEditorStore = data.callerInjector?.get(WorkflowEditorStore);
    this.availableFormFields = workflowEditorStore?.workflow?.form?.fields ?? [];

    this.templates = data.templates;
    this.original = data.item;
    this.result = this.original ?? undefined;

    this.form = fb.group({
      template: [{value: this.result?.type, disabled: false}, Validators.required],
    });
    this.formOptionsGroup = this.buildFormOptionsSection(this.result, fb);

    this.form.get('template')?.valueChanges.subscribe(value => {
      const template = this.templates.find(t => t.type === value);
      if (!template) { return; }

      // We don't have result so we may need to build the options
      if (!this.result) {
        this.result = template.evaluateToAction(value.options);
        this.formOptionsGroup = this.buildFormOptionsSection(this.result, fb);
        return;
      }

      // We are changing the template, we need to reset & rebuild
      if (this.result.type !== template.type) {
        this.formOptionsGroup = undefined;
        this.result = template.evaluateToAction(value.options, this.result.id);
        this.formOptionsGroup = this.buildFormOptionsSection(this.result, fb);
        return;
      }
    });
  }

  ngOnInit(): void { }

  ngAfterViewInit(): void { }

  // Build/Remove options section of the form if we need to
  private buildFormOptionsSection(action: TransitionAction|undefined, fb: FormBuilder): FormGroup|undefined {
    if (action === undefined) { return undefined; }
    if (action.options === undefined) { return undefined; }

    const value = this.formValueFromAction(action);

    const controls = action.options.reduce<{[key: string]: FormControl|FormArray}>((result, option) => {
      if (option.type === 'wysiwyg') {
        result[option.id] = fb.control(value.options[option.id], [invalidPlaceholders()]);
      } else {
        result[option.id] = fb.control(value.options[option.id]);
      }
      return result;
    }, {});

    return fb.group(controls);
  }

  private formValueFromAction(action: TransitionAction): any {
    const options: TransitionActionData = {};
    const data: TransitionActionData = JSON.parse(JSON.stringify(action.data ?? {}));
    // tslint:disable-next-line: forin
    for (const key in data) {
      switch (key) {
        case 'refund_options':
          for (let index = 0; index < (data[key] as any).length; index++) {
            const element = (data[key] as any)[index];
            if (element['metadata'] !== undefined) {
              (data[key] as any)[index]['metadata'] = element['metadata'];
            }
          }
          options[key] = { 'options': data[key] };
          break;
        case 'fields':
          options[key] = { 'options': data[key] };
          break;
        default:
          options[key] = data[key];
          break;
      }
    }

    return { template: action.type, 'options': options };
  }

  private formValueToAction(action: TransitionAction, value: any): TransitionAction|undefined {
    const data = JSON.parse(JSON.stringify(value.options ?? {}));
    // tslint:disable-next-line: forin
    for (const key in data) {
      switch (key) {
        case 'refund_options':
          // Refund options can be optional, so if we don't have anything we return empty array.
          if (!data[key]) {
            data[key] = [];
            break;
          }

          // Otherwise decode the options into the array in the payload
          for (let index = 0; index < (data[key].options as any).length; index++) {
            const element = (data[key].options as any)[index];
            if (element['metadata'] !== undefined) {
              (data[key].options as any)[index]['metadata'] = element['metadata'];
            }
          }
          data[key] = data[key].options;
          break;
        case 'fields':
          data[key] = data[key].options;
          break;
        default:
          break;
      }
    }

    action.data = data;
    return action;
  }

  //
  // Actions
  //

  public onSave(): void {
    if (this.result) {
      this.result.data = this.form.value.options;
    }
    const result = this.result
                   ? this.formValueToAction(this.result, this.form.value)
                   : undefined;
    this.dialogRef.close(result);
  }

  public onCancel(): void {
    this.dialogRef.close(undefined);
  }

  public onDelete(): void {
    if (!this.result) { return; }
    this.dialogRef.close(this.result.id);
  }

  //
  // Template helpers
  //

  public get isAdding(): boolean {
    return !this.original;
  }

  public get shouldShowOptions(): boolean {
    return this.result !== undefined;
  }

  public get shouldShowDelete(): boolean {
    return !this.isAdding;
  }

  public get shouldShowSave(): boolean {
    return this.isAdding || (!this.isAdding && (this.result?.hasOptions ?? false));
  }

  public optionsTemplateContext(): any {
    return {
      result: this.result,
      formGroup: this.formOptionsGroup,
      availableFormFields: this.availableFormFields
    };
  }

  getDialogTitle() {
    return this.isAdding ? $localize `ADD NEW ACTION` : $localize `EDIT ACTION`;
  }

  getAddOrApply() {
    return this.isAdding ? $localize `Add` : `Apply`;
  }
}
